<script lang="ts">
export default {
  name: 'AddPromptMappingModal',
};
</script>

<script setup lang="ts">
import { ref, computed } from 'vue';
import Dialog from '@/components/ui/dialog/Dialog.vue';
import DialogContent from '@/components/ui/dialog/DialogContent.vue';
import DialogHeader from '@/components/ui/dialog/DialogHeader.vue';
import DialogTitle from '@/components/ui/dialog/DialogTitle.vue';
import DialogFooter from '@/components/ui/dialog/DialogFooter.vue';
import UIButton from '@/components/ui/UIButton.vue';
import Combobox from '@/components/v2/Combobox.vue';
import type { ComboboxOption } from '@/components/v2/Combobox.vue';
import type { AIPrompt, AIPromptMapping, AIPromptMappingRequest, AIPromptMappingUpdateRequest } from '@/types/ai';
import { aiService } from '@/services/AIService';

interface CoverageType {
  id: number;
  name: string;
}

interface DocumentType {
  id: number;
  name: string;
}

const props = defineProps<{
  prompts: AIPrompt[];
  coverageTypes: CoverageType[];
  documentTypes: DocumentType[];
}>();

const emit = defineEmits<{
  close: [];
  saved: [];
}>();

const isOpen = ref(false);
const isLoading = ref(false);
const isEditMode = ref(false);
const editMappingId = ref<number | null>(null);
const selectedPrompt = ref('');
const selectedCoverageTypeId = ref('');
const selectedDocumentTypeId = ref('');

// Convert prompts to combobox options
const promptOptions = computed<ComboboxOption[]>(() =>
  props.prompts.map((prompt) => ({
    label: `${prompt.name} (v${prompt.version})`,
    value: `${prompt.promptId}:${prompt.version}`,
  })),
);

// Convert coverage types to combobox options
const coverageTypeOptions = computed<ComboboxOption[]>(() =>
  props.coverageTypes.map((type) => ({
    label: type.name,
    value: type.id.toString(),
  })),
);

// Convert document types to combobox options
const documentTypeOptions = computed<ComboboxOption[]>(() =>
  props.documentTypes.map((type) => ({
    label: type.name,
    value: type.id.toString(),
  })).sort((a, b) => a.label.localeCompare(b.label))
);

const isFormValid = computed(
  () => selectedPrompt.value && selectedCoverageTypeId.value && selectedDocumentTypeId.value,
);

const show = (mapping?: AIPromptMapping) => {
  if (mapping) {
    isEditMode.value = true;
    editMappingId.value = mapping.id;
    selectedPrompt.value = `${mapping.promptId}:${mapping.promptVersion}`;
    selectedCoverageTypeId.value = mapping.coverageTypeId.toString();
    selectedDocumentTypeId.value = mapping.documentTypeId.toString();
  }
  isOpen.value = true;
};

const hide = () => {
  isOpen.value = false;
  resetForm();
  emit('close');
};

const resetForm = () => {
  selectedPrompt.value = '';
  selectedCoverageTypeId.value = '';
  selectedDocumentTypeId.value = '';
  isLoading.value = false;
  isEditMode.value = false;
  editMappingId.value = null;
};

const handleSave = async () => {
  if (!isFormValid.value) return;

  isLoading.value = true;
  try {
    if (isEditMode.value && editMappingId.value !== null) {
      const request: AIPromptMappingUpdateRequest = {
        promptId: Number(selectedPrompt.value.split(':')[0]),
        promptVersion: selectedPrompt.value.split(':')[1],
        coverageTypeId: parseInt(selectedCoverageTypeId.value),
        documentTypeId: parseInt(selectedDocumentTypeId.value),
      };
      await aiService.updatePromptMapping(editMappingId.value, request);
    } else {
      const request: AIPromptMappingRequest = {
        promptId: Number(selectedPrompt.value.split(':')[0]),
        promptVersion: selectedPrompt.value.split(':')[1],
        coverageTypeId: parseInt(selectedCoverageTypeId.value),
        documentTypeId: parseInt(selectedDocumentTypeId.value),
      };
      await aiService.createPromptMapping(request);
    }
    hide();
    emit('saved');
  } catch (error) {
    console.error(`Failed to ${isEditMode.value ? 'update' : 'create'} prompt mapping:`, error);
  } finally {
    isLoading.value = false;
  }
};

// Expose the show method for parent component
defineExpose({
  show,
});
</script>

<template>
  <Dialog :open="isOpen">
    <DialogContent
      class="tw:max-w-md tw:bg-white tw:data-[state=closed]:animate-dialog-hide tw:data-[state=open]:animate-dialog-show">
      <DialogHeader>
        <DialogTitle>{{ isEditMode ? 'Edit Prompt Mapping' : 'New Prompt Mapping' }}</DialogTitle>
      </DialogHeader>

      <div class="tw:space-y-6">
        <div class="tw:space-y-2">
          <label class="tw:block tw:text-sm tw:font-medium tw:text-slate-700">
            Prompt <span class="tw:text-red-500">*</span>
          </label>
          <Combobox
            v-model="selectedPrompt"
            :options="promptOptions"
            placeholder="Select a prompt..."
            class="tw:w-full"
            inputClass="tw:input-sm" />
          <p class="tw:text-xs tw:text-slate-500">Choose the prompt to associate with the coverage and document type</p>
        </div>

        <div class="tw:space-y-2">
          <label class="tw:block tw:text-sm tw:font-medium tw:text-slate-700">
            Coverage Type <span class="tw:text-red-500">*</span>
          </label>
          <Combobox
            v-model="selectedCoverageTypeId"
            :options="coverageTypeOptions"
            placeholder="Select coverage type..."
            class="tw:w-full"
            inputClass="tw:input-sm" />
          <p class="tw:text-xs tw:text-slate-500">Select the type of insurance coverage this mapping applies to</p>
        </div>

        <div class="tw:space-y-2">
          <label class="tw:block tw:text-sm tw:font-medium tw:text-slate-700">
            Document Type <span class="tw:text-red-500">*</span>
          </label>
          <Combobox
            v-model="selectedDocumentTypeId"
            :options="documentTypeOptions"
            placeholder="Select document type..."
            class="tw:w-full"
            inputClass="tw:input-sm" />
          <p class="tw:text-xs tw:text-slate-500">Select the type of document this mapping applies to</p>
        </div>
      </div>

      <DialogFooter>
        <UIButton variant="outline" @click="hide" :disabled="isLoading"> Cancel </UIButton>
        <UIButton @click="handleSave" :disabled="!isFormValid || isLoading">
          <span v-if="isLoading">{{ isEditMode ? 'Saving...' : 'Creating...' }}</span>
          <span v-else>{{ isEditMode ? 'Save' : 'Create' }}</span>
        </UIButton>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
